// |getConversationOverview|sbsQueryConversationList|sbsQueryConversation|conversation/dates|

import { extractFailCauseFromLastMsgContent } from 'services/api/conversation.convList';
import { QueryConversationParams } from 'services/api/conversation';
import { doorClient, userVoiceClient, ImResponse, resolve } from '.';

export interface SBSReportOverviewRes {
  conversationPerformance: {
    conversationCount: number; // 咨询会话量
    humanAssistantReplyRatio: number; // 人工客服回复率
    humanReplyTimeSecond: number; // 人工客服平均响应时长，单位：s
    humanWithBotReplyRatio: number; // 人机回复率
    humanWithBotReplyTimeSecond: number; // 人机平均响应时长，单位：s
    botReplyAccuracy: number; // 机器人回复准确率
    conversationSavedResource: number; // 会话接待节省人天数
    buyerQuestionCount: number; // 买家咨询问题条数
    botReplyQuestionCount: number; // 机器人回复问题条数
    llmReplyQuestionCount: number;
  };
  orderReminderPerformance: {
    beforeSaleConsultBuyerCount: number; // 售前咨询买家数
    humanOrderReminderBuyerCount: number; // 客服催下单买家数
    botOrderReminderBuyerCount: number; // 机器人催下单买家数
    botOrderReminderConvertAmount: { botConvertAmount: number }; // 机器人催单后下单总金额
    botOrderReminderSavedResource: number; // 催下单节省人天数
  };
  paymentReminderPerformance: {
    nonCodUnpaidOrderCount: number; // 非cod下单未付款订单量
    humanPaymentReminderOrderCount: number; // 客服催付款订单量
    botPaymentReminderOrderCount: number; // 机器人催付款订单量
    botPaymentReminderConvertAmount: { botConvertAmount: number }; // 机器人催付后付款总金额
    botPaymentReminderSavedResource: number; // 催付款节省人天数
  };
  cancelOrderReminderPerformance: {
    cancelledOrderCount: number; // 买家取消订单量
    humanCancelOrderReminderOrderCount: number; // 客服挽回取消订单量
    botCancelOrderReminderOrderCount: number; // 机器人挽回取消订单量
    botCancelOrderReminderConvertAmount: { botConvertAmount: number }; // 机器人挽回取消订单总金额
    botCancelOrderReminderSavedResource: number; // 取消订单挽回节省人天数
  };
  praiseReminderPerformance: {
    completeOrderCount: number; // 已完成订单量(待催好评订单量)
    humanPraiseReminderOrderCount: number; // 客服催好评订单量
    botPraiseReminderOrderCount: number; // 机器人催好评订单量
    botPraiseReminderConvertRatio: number; // 机器人催好评转化率
    botPraiseReminderSavedResource: number; // 催好评节省人天数
  };
  autoReviewPerformance: {
    toAutoReviewCount: number; // 买家已进行评价的数量
    humanReplyReviewCount: number; // 人工客服回评数量
    botReplyReviewCount: number; // 机器人回评数量
    botAutoReviewSavedResource: number; // 自动回评节省人天数
  };
}

export function getAllowedDateRange(reportId: string) {
  return resolve(
    userVoiceClient.get<
      ImResponse<
        [
          {
            startDate: string;
            endDate: string;
          },
        ]
      >
    >('/v1/sbs/conversation/dates', { params: { reportId } }),
  );
}

export function getSBSTopConsultScenario(params: {
  date: string;
  reportId: string;
}): Promise<Array<{ scenarioName: string; ratio: number }>> {
  return resolve(doorClient.post('/oversea-user-voice/v1/sbs/query-scenario-data', params));
}

export function getSBSReportOverview(params: {
  date: string;
  reportId: string;
}): Promise<SBSReportOverviewRes> {
  return resolve(doorClient.post('/oversea-user-voice/v1/sbs/query-bot-performance', params));
}

export interface IConversationOverview {
  /** 当日咨询会话总量 */
  totalConversations: string;
  /** 机器人参与接待量 */
  botParticipates: string;
  /** 机器人未参与接待量 */
  botUnParticipates: string;
  /** 机器人催下单量 */
  botOrderReminds: string;
  /** 机器人催付款量 */
  botPaymentReminds: string;
  /** 机器人取消订单挽回量 */
  botCancelOrderReminds: string;
  /** 机器人催好评量 */
  botPraiseReminds: string;
}

export function getConversationOverview(date: string, reportId: string) {
  return resolve(
    userVoiceClient.get<ImResponse<IConversationOverview>>('/v1/sbs/conversation/overview', {
      params: { date, reportId },
    }),
  );
}

/**
 ```
 ALL = 0; 全部
 PARTICIPATES = 1; 有机器人参与
 UNPARTICIPATES = 2; 没有机器人参与
 ORDER_REMINDS = 3; 有催下单
 PAYMENT_REMINDS = 4; 有催付款
 CANCEL_ORDER_REMINDS = 5; 有取消订单
 PRAISE_REMINDS = 6; 有催好评
 ```
 */
export type SbsConversationStatsType =
  | 'ALL'
  | 'PARTICIPATES'
  | 'UNPARTICIPATES'
  | 'ORDER_REMINDS'
  | 'PAYMENT_REMINDS'
  | 'CANCEL_ORDER_REMINDS'
  | 'PRAISE_REMINDS';

export type SbsConversationGroupType =
  | '乐聊竞品均回复'
  | '乐聊回复竞品未回复'
  | '竞品回复乐聊未回复'
  | '乐聊竞品均未回复';

export type SbsReplyAccurateType = 'ALL_REPLY' | 'ACCURATE' | 'INACCURATE';

export interface SbsConversationListParams extends ApiConversation.ConversationListParams {
  sbsReportId: string;
  sbsReportDate: string;
  sbsConversationStatsType: SbsConversationStatsType;
  sbsLeyanReply?: SbsReplyAccurateType;
  sbsOtherReply?: SbsReplyAccurateType;
  sbsKeyword?: string;
  convGroup?: SbsConversationGroupType;
}

export function sbsQueryConversationList(params: SbsConversationListParams) {
  return resolve(
    doorClient.get<ImResponse<ApiConversation.ConversationListResponse>>(
      '/oversea-sbs-report/v1/queryConversationList',
      {
        params,
        transformResponse(data) {
          try {
            const json = JSON.parse(data);
            return {
              ...json,
              data: {
                ...json.data,
                talkInfo: json.data.talkInfo.map(extractFailCauseFromLastMsgContent),
              },
            };
          } catch (error) {
            return data;
          }
        },
      },
    ),
  );
}

export interface SbsQueryConversationParams extends QueryConversationParams {
  talkId: string;
}

export function sbsQueryConversation(params: SbsQueryConversationParams) {
  return doorClient
    .get<ImResponse<{ message?: Conversation.Message[] }>>(
      '/oversea-sbs-report/v1/queryConversation',
      {
        params,
      },
    )
    .then((res) => {
      res.data.data.message = res.data.data.message?.map((message) => {
        try {
          const extraInfo = message.extra
            ? (JSON.parse(message.extra) as Conversation.ExtraInfo)
            : undefined;
          return {
            ...message,
            extraInfo,
          };
        } catch (error) {
          return message;
        }
      });
      return res;
    });
}

interface Accurate {
  count: number;
  otherAccurate: {
    count: number;
  };
  otherInaccurate: {
    count: number;
  };
  otherSilent: {
    count: number;
  };
}

export interface OverviewCompareData {
  allLeyanReplyCount: number;
  allQueryCount: number;
  sampleCommonReplyCount: number;
  sampleLeyanAccuracyCount: number;
  sampleLeyanReplyCount: number;
  sampleNoneReplyCount: number;
  sampleOnlyLeyanAccuracyCount: number;
  sampleOnlyLeyanReplyCount: number;
  sampleOnlyOtherAccuracyCount: number;
  sampleOnlyOtherReplyCount: number;
  sampleOtherAccuracyCount: number;
  sampleOtherReplyCount: number;
  sampleQueryCount: number;
  allConversationCount: number;
  sampleConversationCount: number;
  replyAccuracyMatrix: {
    leyanAccurate: Accurate;
    leyanInaccurate: Accurate;
    leyanSilent: Accurate;
  };
}

export function getConversationOverviewCompare(params: { date: string; reportId: string }) {
  return resolve(
    userVoiceClient.get<ImResponse<OverviewCompareData>>('/v1/sbs/conversation/overview_compare', {
      params,
    }),
  );
}
